import React from 'react';
import { Mic, BookOpen, Sparkles, TrendingUp, Calendar, Award, ArrowRight, Clock, Play, Zap, Lightbulb, CheckCircle, Flame, Star, Users } from 'lucide-react';
type HomeDesignBProps = {
  onNavigate?: (page: string) => void;
  onViewStory?: (storyId: number) => void;
};
export function HomeDesignB({
  onNavigate,
  onViewStory
}: HomeDesignBProps) {
  const quickStats = [{
    label: 'Total Stories',
    value: '5',
    change: '+2 this week',
    icon: BookOpen,
    color: 'green'
  }, {
    label: 'Storybooks',
    value: '5',
    change: '+1 this week',
    icon: Sparkles,
    color: 'purple'
  }, {
    label: 'Recording Time',
    value: '47m',
    change: '+12m this week',
    icon: Clock,
    color: 'blue'
  }, {
    label: 'Credits',
    value: '35',
    change: '15 remaining',
    icon: Zap,
    color: 'amber'
  }];
  const suggestions = [{
    id: 1,
    title: 'Create a storybook from "Summer at the Lake House"',
    description: 'This story has great visuals - perfect for an illustrated book!',
    action: 'Create Now',
    icon: Sparkles,
    color: 'purple',
    priority: 'high'
  }, {
    id: 2,
    title: "You haven't recorded in 5 days",
    description: 'Try this prompt: "Tell me about your first job"',
    action: 'Start Recording',
    icon: Mic,
    color: 'green',
    priority: 'medium'
  }, {
    id: 3,
    title: 'Add AI summary to "Dad\'s War Stories"',
    description: 'Get an instant summary and key moments',
    action: 'Generate Summary',
    icon: Lightbulb,
    color: 'amber',
    priority: 'low'
  }];
  const activityFeed = [{
    id: 1,
    text: 'You recorded "Summer Memories"',
    time: '2 hours ago',
    icon: Mic,
    color: 'green'
  }, {
    id: 2,
    text: 'Storybook "Achilles" completed',
    time: 'Yesterday',
    icon: Sparkles,
    color: 'purple'
  }, {
    id: 3,
    text: 'Sarah viewed "Family Recipe"',
    time: '2 days ago',
    icon: Play,
    color: 'blue'
  }, {
    id: 4,
    text: 'You earned "Storyteller" badge',
    time: '3 days ago',
    icon: Award,
    color: 'amber'
  }];
  const goals = [{
    title: 'Record 5 stories this month',
    current: 3,
    target: 5,
    color: 'green',
    emoji: '🎤'
  }, {
    title: 'Create your first storybook',
    current: 0,
    target: 1,
    color: 'purple',
    emoji: '📚'
  }, {
    title: 'Invite a family member',
    current: 0,
    target: 1,
    color: 'blue',
    emoji: '👥'
  }];
  const recentStories = [{
    id: 1,
    title: "Grandma's Secret Recipe",
    thumbnail: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=400&h=300&fit=crop',
    duration: '8:24',
    status: 'Ready for storybook',
    statusColor: 'green'
  }, {
    id: 2,
    title: 'Our First Road Trip',
    thumbnail: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=400&h=300&fit=crop',
    duration: '12:35',
    status: 'New',
    statusColor: 'blue'
  }, {
    id: 3,
    title: 'Summer at the Lake House',
    thumbnail: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop',
    duration: '6:18',
    status: 'Needs summary',
    statusColor: 'amber'
  }];
  const achievements = [{
    id: 1,
    title: 'First Story',
    icon: '🎤',
    unlocked: true
  }, {
    id: 2,
    title: 'Storyteller',
    icon: '📚',
    unlocked: true
  }, {
    id: 3,
    title: 'Author',
    icon: '✨',
    unlocked: true
  }, {
    id: 4,
    title: 'Prolific',
    icon: '🏆',
    unlocked: false
  }];
  return <div className="flex-1 bg-gray-100 min-h-screen" data-id="element-848">
      <div className="px-8 py-8" data-id="element-849">
        {/* Hero CTA Section - Variant with Split Layout */}
        <div className="grid grid-cols-3 gap-6 mb-8" data-id="element-850">
          {/* Main CTA - Takes 2 columns */}
          <div className="col-span-2 bg-gradient-to-br from-green-500 via-green-600 to-emerald-600 rounded-3xl p-10 shadow-xl relative overflow-hidden" data-id="element-851">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" data-id="element-852"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24" data-id="element-853"></div>
            <div className="relative z-10" data-id="element-854">
              <div className="flex items-center gap-3 mb-4" data-id="element-855">
                <div className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium" data-id="element-856">
                  <Flame className="w-4 h-4" data-id="element-857" />7 day streak!
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium" data-id="element-858">
                  <Star className="w-4 h-4" data-id="element-859" />
                  Top 10% this week
                </div>
              </div>
              <h1 className="text-5xl font-bold text-white mb-4" data-id="element-860">
                Ready to create?
              </h1>
              <p className="text-green-100 text-xl mb-8 max-w-xl" data-id="element-861">
                You're just 2 stories away from unlocking your first collection
                milestone
              </p>
              <div className="flex gap-4" data-id="element-862">
                <button className="px-8 py-4 bg-white text-green-600 font-semibold rounded-xl hover:bg-green-50 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center gap-2" data-id="element-863">
                  <Mic className="w-5 h-5" data-id="element-864" />
                  Record Story
                </button>
                <button className="px-8 py-4 bg-green-700 text-white font-semibold rounded-xl hover:bg-green-800 transition-all flex items-center gap-2" data-id="element-865">
                  <Sparkles className="w-5 h-5" data-id="element-866" />
                  Create Storybook
                </button>
              </div>
            </div>
          </div>

          {/* Quick Stats - Stacked */}
          <div className="space-y-4" data-id="element-867">
            {quickStats.map(stat => <div key={stat.label} className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow" data-id="element-868">
                <div className="flex items-center justify-between mb-2" data-id="element-869">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-${stat.color}-100`} data-id="element-870">
                    <stat.icon className="w-5 h-5" data-id="element-871" />
                  </div>
                  <TrendingUp className="w-4 h-4 text-green-600" data-id="element-872" />
                </div>
                <div className="text-2xl font-bold text-gray-900" data-id="element-873">
                  {stat.value}
                </div>
                <div className="text-xs text-gray-600" data-id="element-874">{stat.label}</div>
                <div className="text-xs text-green-600 font-medium mt-1" data-id="element-875">
                  {stat.change}
                </div>
              </div>)}
          </div>
        </div>

        {/* Smart Suggestions - Horizontal Cards */}
        <div className="mb-8" data-id="element-876">
          <div className="flex items-center justify-between mb-4" data-id="element-877">
            <div className="flex items-center gap-2" data-id="element-878">
              <Sparkles className="w-6 h-6 text-purple-600" data-id="element-879" />
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-880">
                Suggested Actions
              </h2>
            </div>
            <button className="text-sm text-gray-600 hover:text-gray-900" data-id="element-881">
              Dismiss all
            </button>
          </div>
          <div className="space-y-3" data-id="element-882">
            {suggestions.map(suggestion => <div key={suggestion.id} className={`bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-all cursor-pointer border-l-4 border-${suggestion.color}-500`} data-id="element-883">
                <div className="flex items-center gap-4" data-id="element-884">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center bg-${suggestion.color}-100 flex-shrink-0`} data-id="element-885">
                    <suggestion.icon className={`w-7 h-7 text-${suggestion.color}-600`} data-id="element-886" />
                  </div>
                  <div className="flex-1" data-id="element-887">
                    <div className="flex items-center gap-2 mb-1" data-id="element-888">
                      <h3 className="font-semibold text-gray-900" data-id="element-889">
                        {suggestion.title}
                      </h3>
                      {suggestion.priority === 'high' && <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-medium rounded" data-id="element-890">
                          High Priority
                        </span>}
                    </div>
                    <p className="text-sm text-gray-600" data-id="element-891">
                      {suggestion.description}
                    </p>
                  </div>
                  <button className={`px-6 py-3 bg-${suggestion.color}-500 text-white font-medium rounded-xl hover:bg-${suggestion.color}-600 transition-colors flex-shrink-0`} data-id="element-892">
                    {suggestion.action}
                  </button>
                </div>
              </div>)}
          </div>
        </div>

        {/* Three Column Layout */}
        <div className="grid grid-cols-3 gap-6 mb-8" data-id="element-893">
          {/* Activity Feed */}
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-894">
            <div className="flex items-center gap-2 mb-6" data-id="element-895">
              <Clock className="w-5 h-5 text-gray-600" data-id="element-896" />
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-897">Activity</h3>
            </div>
            <div className="space-y-4" data-id="element-898">
              {activityFeed.map(item => <div key={item.id} className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-899">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center bg-${item.color}-100 flex-shrink-0`} data-id="element-900">
                    <item.icon className={`w-5 h-5 text-${item.color}-600`} data-id="element-901" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-902">
                    <p className="text-sm font-medium text-gray-900 line-clamp-2" data-id="element-903">
                      {item.text}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-904">{item.time}</p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Goals & Progress */}
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-905">
            <div className="flex items-center gap-2 mb-6" data-id="element-906">
              <Award className="w-5 h-5 text-gray-600" data-id="element-907" />
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-908">
                Your Goals
              </h3>
            </div>
            <div className="space-y-5" data-id="element-909">
              {goals.map((goal, index) => <div key={index} data-id="element-910">
                  <div className="flex items-center gap-2 mb-2" data-id="element-911">
                    <span className="text-2xl" data-id="element-912">{goal.emoji}</span>
                    <span className="text-sm font-medium text-gray-900 flex-1" data-id="element-913">
                      {goal.title}
                    </span>
                    <span className="text-xs text-gray-600" data-id="element-914">
                      {goal.current}/{goal.target}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-915">
                    <div className={`h-full bg-${goal.color}-500 rounded-full transition-all duration-500`} style={{
                  width: `${goal.current / goal.target * 100}%`
                }} data-id="element-916" />
                  </div>
                </div>)}
            </div>
          </div>

          {/* Achievements */}
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-917">
            <div className="flex items-center gap-2 mb-6" data-id="element-918">
              <Star className="w-5 h-5 text-gray-600" data-id="element-919" />
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-920">Badges</h3>
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-921">
              {achievements.map(achievement => <div key={achievement.id} className={`p-4 rounded-xl text-center transition-all ${achievement.unlocked ? 'bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-200' : 'bg-gray-50 opacity-50'}`} data-id="element-922">
                  <div className="text-4xl mb-2" data-id="element-923">{achievement.icon}</div>
                  <div className="text-xs font-medium text-gray-900" data-id="element-924">
                    {achievement.title}
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* Recent Stories */}
        <div className="mb-8" data-id="element-925">
          <div className="flex items-center justify-between mb-6" data-id="element-926">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-927">Recent Stories</h2>
            <button className="text-sm font-medium text-green-600 hover:text-green-700 flex items-center gap-1" data-id="element-928">
              View all
              <ArrowRight className="w-4 h-4" data-id="element-929" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-930">
            {recentStories.map(story => <div key={story.id} onClick={() => onViewStory?.(story.id)} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer hover:-translate-y-1" data-id="element-931">
                <div className="relative aspect-video overflow-hidden" data-id="element-932">
                  <img src={story.thumbnail} alt={story.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-933" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-934">
                    <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center" data-id="element-935">
                      <Play className="w-6 h-6 text-gray-900 ml-0.5" data-id="element-936" />
                    </div>
                  </div>
                  <div className="absolute top-3 left-3" data-id="element-937">
                    <span className={`px-3 py-1 bg-${story.statusColor}-500 text-white text-xs font-medium rounded-full`} data-id="element-938">
                      {story.status}
                    </span>
                  </div>
                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/75 text-white text-xs font-medium rounded backdrop-blur-sm" data-id="element-939">
                    {story.duration}
                  </div>
                </div>
                <div className="p-4" data-id="element-940">
                  <h3 className="font-semibold text-gray-900 line-clamp-1" data-id="element-941">
                    {story.title}
                  </h3>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}